import React, { useRef, useEffect, useState } from 'react';
import LiveResumeTemplate from './LiveResumeTemplate';
import SimpleResumePreview from './shared/SimpleResumePreview';

/**
 * 이력서 렌더링을 담당하는 공통 컴포넌트
 * ConsultingPage와 ResumePage에서 완전히 일관된 렌더링을 보장
 */
const ResumeRenderer = ({ 
  resumeData, 
  updatingField, 
  zoomLevel = 100, 
  containerMode = 'auto', // 'auto', 'fixed', 'responsive'
  className = '',
  style = {},
  onContainerRef = null,
  enableAutoScale = false, // 자동 스케일링 활성화 여부
  language = 'kr', // 언어 설정 (기본값: 한국어)
  showTemplateSelector = true, // 템플릿 선택기 표시 여부
  selectedTemplate, // 선택된 템플릿 ID
  onTemplateChange // 템플릿 변경 시 콜백
}) => {
  const containerRef = useRef(null);
  const [autoScale, setAutoScale] = useState(100);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  // 컨테이너 크기 감지 및 자동 스케일링
  useEffect(() => {
    if (!enableAutoScale || !containerRef.current) return;

    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        
        setContainerDimensions({ width: containerWidth, height: containerHeight });
        
        // A4 크기 (210mm x 297mm) at 96dpi
        const a4WidthPx = 794; // 210mm
        const a4HeightPx = 1123; // 297mm
        
        // 컨테이너에 맞는 최적 스케일 계산 (여백 고려)
        const widthScale = (containerWidth * 0.95) / a4WidthPx * 100;
        const heightScale = (containerHeight * 0.95) / a4HeightPx * 100;
        
        // 더 작은 스케일을 선택하여 컨테이너를 벗어나지 않도록 함
        const calculatedScale = Math.min(widthScale, heightScale, 100);
        setAutoScale(Math.max(calculatedScale, 30)); // 최소 30%
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    
    // ResizeObserver로 컨테이너 크기 변화도 감지
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(containerRef.current);
    
    return () => {
      window.removeEventListener('resize', updateScale);
      resizeObserver.disconnect();
    };
  }, [enableAutoScale]);

  // 부모 컴포넌트에 ref 전달
  useEffect(() => {
    if (onContainerRef && containerRef.current) {
      onContainerRef(containerRef.current);
    }
  }, [onContainerRef]);

  /**
   * 완전히 일관된 컨테이너 스타일
   * 모든 모드에서 동일한 A4 크기 사용
   */
  const getContainerStyle = () => {
    const effectiveZoom = enableAutoScale ? autoScale : zoomLevel;
    
    // A4 크기를 픽셀로 고정하여 완전한 일관성 보장
    return {
      transform: `scale(${effectiveZoom / 100})`,
      transformOrigin: 'top center',
      transition: 'transform 0.2s ease-in-out',
      // A4 크기 완전 고정 (794px = 210mm, 1123px = 297mm at 96dpi)
      width: '794px',
      minWidth: '794px', 
      maxWidth: '794px',
      height: 'auto',
      minHeight: '1123px',
      margin: '0 auto',
      display: 'block',
      position: 'relative',
      ...style
    };
  };

  /**
   * 외부 컨테이너 스타일
   */
  const getWrapperStyle = () => {
    return {
      width: '100%',
      minWidth: '860px', // 794px 이력서 + 여백 고려
      minHeight: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      backgroundColor: '#e5e7eb', // 배경색 (gray-200과 동일)
      padding: '1.5rem', // 좋은 여백
      overflow: 'auto'
    };
  };

  return (
    <div 
      ref={containerRef}
      className={`resume-renderer-wrapper ${className}`}
      style={getWrapperStyle()}
    >
      <div
        className="resume-renderer-content"
        style={getContainerStyle()}
        data-pdf-content="true" // PDF 생성을 위한 마커
      >
        <LiveResumeTemplate
          resumeData={resumeData}
          updatingField={updatingField}
          language={language}
          showTemplateSelector={showTemplateSelector}
          selectedTemplate={selectedTemplate}
          onTemplateChange={onTemplateChange}
        />
      </div>
      
      {/* 개발 모드에서 디버그 정보 표시 - 모바일에서는 표시하지 않음 */}
      {process.env.NODE_ENV === 'development' && enableAutoScale && window.innerWidth > 768 && (
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded z-50">
          <div>Mode: {containerMode}</div>
          <div>Manual Zoom: {zoomLevel}%</div>
          <div>Auto Scale: {Math.round(autoScale)}%</div>
          <div>Container: {containerDimensions.width}x{containerDimensions.height}</div>
          <div>A4 Size: 794x1123px</div>
        </div>
      )}
    </div>
  );
};

export default ResumeRenderer;
